import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { Bouton } from './Bouton.js'
import { ErreurVersionObsolete, estErreurApi } from '../api/erreurs.js'
import { signalerErreur } from '../suivi-erreurs.js'
import styles from './FrontiereErreur.module.css'

interface Proprietes {
  readonly children: ReactNode
  /** Ramène à un état sain — typiquement une navigation vers l'accueil. */
  readonly onReinitialiser?: (() => void) | undefined
}

interface Etat {
  readonly erreur: unknown
}

/** Ce que la frontière déduit d'une erreur, avant de choisir quoi afficher. */
export interface DiagnosticErreur {
  /**
   * Le module n'a pas pu être chargé — presque toujours un déploiement
   * pendant que l'onglet était ouvert : les fichiers de l'ancienne version
   * n'existent plus. Réessayer ne sert à rien, il faut recharger.
   */
  readonly chargementInterrompu: boolean
  /** Identifiant de requête à donner au support, quand il y en a un. */
  readonly reference: string | undefined
}

/**
 * Extrait de la classe pour être testable sans DOM.
 *
 * Ce sont les deux décisions que prend la frontière, et les deux qui
 * pourrissent en silence : une formulation de message qui change côté
 * navigateur, et un `requestId` qu'on oublie de remonter. Le rendu, lui,
 * n'est que de la mise en forme.
 */
export function analyser(erreur: unknown): DiagnosticErreur {
  // Les trois navigateurs formulent différemment le même échec.
  //   Chrome/Edge : « Failed to fetch dynamically imported module »
  //   Firefox     : « error loading dynamically imported module »
  //   Safari      : « Importing a module script failed »
  const chargementInterrompu =
    erreur instanceof ErreurVersionObsolete ||
    (erreur instanceof Error &&
      /dynamically imported module|Importing a module script failed|error loading dynamically/i.test(
        erreur.message,
      ))

  return {
    chargementInterrompu,
    reference: estErreurApi(erreur) ? erreur.requestId : undefined,
  }
}

/**
 * Le filet sous les écrans.
 *
 * Sans lui, **toute** exception au rendu donne une page blanche : pas de
 * message, pas de bouton, rien dans l'interface. C'est arrivé en
 * développement et ça a coûté une demi-heure de diagnostic ; en agence, ce
 * serait un agent d'accueil devant un patient, face à un écran vide, sans
 * même une référence à donner au support.
 *
 * Les états « chargement / vide / erreur » de chaque écran couvrent ce que
 * l'écran sait anticiper (skill `frontend-react-afs`). Cette frontière couvre
 * le reste — et c'est justement le reste qui blanchit la page.
 *
 * Une classe, parce qu'il n'existe pas d'équivalent en hook : React n'expose
 * `getDerivedStateFromError` et `componentDidCatch` que sur des composants de
 * classe.
 */
export class FrontiereErreur extends Component<Proprietes, Etat> {
  override state: Etat = { erreur: null }

  static getDerivedStateFromError(erreur: unknown): Etat {
    return { erreur }
  }

  override componentDidCatch(erreur: unknown, info: ErrorInfo): void {
    // On journalise le TYPE et la pile de composants — jamais le message.
    //
    // Le message peut avoir été construit à partir d'une donnée patient
    // (« Patient RAKOTO introuvable »), et la console d'un poste d'agence
    // est lue par des gens sans droit d'accès médical
    // (skill `securite-donnees-sante` § Logs). Le message reste affiché à
    // l'écran, à celui qui a déjà le droit de le voir.
    const type = erreur instanceof Error ? erreur.name : typeof erreur
    console.error(`[frontière] rendu interrompu : ${type}`, info.componentStack)
    // Le suivi des erreurs, lui, reçoit l'erreur entière : son message est
    // expurgé avant le départ (ADR 0013). Sauf un chargement interrompu par
    // un déploiement : chaque onglet ouvert en produit un, c'est du bruit et
    // pas un défaut (décision du Tech Lead, 16/10).
    if (!analyser(erreur).chargementInterrompu) signalerErreur(erreur)
  }

  private readonly reessayer = (): void => {
    this.setState({ erreur: null })
    this.props.onReinitialiser?.()
  }

  private readonly recharger = (): void => {
    window.location.reload()
  }

  override render(): ReactNode {
    const { erreur } = this.state
    if (erreur === null) return this.props.children

    const { chargementInterrompu, reference } = analyser(erreur)

    return (
      <div className={styles.ecran} role="alert">
        <div className={styles.carte}>
          <h1 className={styles.titre}>
            {chargementInterrompu
              ? 'Une nouvelle version est disponible'
              : 'Cet écran s’est interrompu'}
          </h1>

          <p className={styles.explication}>
            {chargementInterrompu
              ? 'La plateforme a été mise à jour pendant que cet onglet était ouvert. Rechargez la page pour continuer — rien n’est perdu.'
              : 'Rien n’a été enregistré. Vous pouvez réessayer ; si l’écran s’interrompt à nouveau, signalez-le au support avec la référence ci-dessous.'}
          </p>

          {reference !== undefined && (
            <p className={styles.reference}>Référence à communiquer : {reference}</p>
          )}

          {/*
            Le détail technique n'apparaît qu'en développement. En agence, il
            n'aiderait personne et pourrait exposer un fragment de donnée
            repris dans le message.
          */}
          {import.meta.env.DEV && erreur instanceof Error && (
            <pre className={styles.detail}>{erreur.message}</pre>
          )}

          <div className={styles.actions}>
            {chargementInterrompu ? (
              <Bouton onClick={this.recharger}>Recharger la page</Bouton>
            ) : (
              <>
                <Bouton onClick={this.reessayer}>Réessayer</Bouton>
                <Bouton variante="secondaire" onClick={this.recharger}>
                  Recharger la page
                </Bouton>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }
}
